"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { Check, ChevronLeft, Copy, Share2, Users } from "lucide-react"
import { Button } from "~/components/ui/button"
import { Card, CardContent } from "~/components/ui/card"
import { Input } from "~/components/ui/input"
import { Skeleton } from "~/components/ui/skeleton"
import { createClient } from "~/lib/supabase/client"
import { consumeReferralCode } from "~/lib/referral-capture"
import { env } from "~/env"
import { withImpersonation } from '~/lib/impersonation-client'

interface ReferralSummary {
  referral_code: string
  referred_count: number
  referred_paid_count: number
}

function shareUrl(code: string): string {
  if (typeof window === "undefined") return `?ref=${code}`
  return `${window.location.origin}/login?ref=${code}`
}

export default function InvitePage() {
  const router = useRouter()
  const [summary, setSummary] = useState<ReferralSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sharedNatively, setSharedNatively] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const supabase = createClient()
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) {
          router.push("/login")
          return
        }
        const authHeader = { Authorization: `Bearer ${withImpersonation(session.access_token)}` }

        // S-3 — OAuth signups can't carry `?ref=` through Supabase metadata.
        // If we have a captured code in sessionStorage, hand it off via the
        // /apply endpoint. Idempotent and safe to call repeatedly.
        const pending = consumeReferralCode()
        if (pending) {
          await fetch(`${env.NEXT_PUBLIC_API_URL}/me/referral/apply`, {
            method: "POST",
            headers: { ...authHeader, "Content-Type": "application/json" },
            body: JSON.stringify({ code: pending }),
          }).catch(() => {
            // Already referred / self / unknown all come back as
            // { attached: false }; surface to the user via the summary only.
          })
        }

        const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/me/referral`, {
          headers: authHeader,
        })
        if (!res.ok) throw new Error("Failed to load your invite link")
        setSummary(await res.json())
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load your invite link")
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [router])

  const link = summary ? shareUrl(summary.referral_code) : ""

  const handleCopy = async () => {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard denied — the user can still long-press the input.
    }
  }

  const handleShare = async () => {
    if (!link || typeof navigator === "undefined") return
    const nav = navigator as Navigator & {
      share?: (data: ShareData) => Promise<void>
    }
    if (!nav.share) {
      await handleCopy()
      return
    }
    try {
      await nav.share({
        title: "Join me on Path Collective",
        text: "I'm using Path Collective to find conscious facilitators — join me and we both earn PathCoins on your first booking.",
        url: link,
      })
      setSharedNatively(true)
      setTimeout(() => setSharedNatively(false), 2000)
    } catch {
      // User cancelled the share sheet — nothing to do.
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-8">
        <div className="mb-6">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/me">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to account
            </Link>
          </Button>
        </div>

        <header className="mb-8 space-y-2">
          <h1 className="flex items-center gap-2 text-3xl font-semibold text-neutral-emphasis">
            <Users className="h-7 w-7 text-primary" />
            Invite a friend
          </h1>
          <p className="text-sm text-neutral">
            Share your link. When a friend signs up and books their first paid
            event, you both earn PathCoins.
          </p>
        </header>

        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : loading ? (
          <Card>
            <CardContent className="p-6 space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-32" />
            </CardContent>
          </Card>
        ) : summary ? (
          <>
            <Card className="mb-6 border-primary/20 bg-gradient-to-br from-[#F4F8F6] to-white">
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-sm font-medium text-neutral">Your invite link</p>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <Input
                      readOnly
                      value={link}
                      data-testid="invite-link"
                      className="font-mono text-sm"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCopy}
                        data-testid="invite-copy"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        <span className="ml-2">{copied ? "Copied" : "Copy"}</span>
                      </Button>
                      <Button
                        type="button"
                        onClick={handleShare}
                        data-testid="invite-share"
                      >
                        <Share2 className="h-4 w-4" />
                        <span className="ml-2">{sharedNatively ? "Shared" : "Share"}</span>
                      </Button>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-neutral">
                  Your code: <span className="font-mono font-semibold">{summary.referral_code}</span>
                </p>
              </CardContent>
            </Card>

            <section className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-wide text-neutral">Friends invited</p>
                  <p
                    className="mt-1 text-3xl font-semibold text-neutral-emphasis"
                    data-testid="invite-count"
                  >
                    {summary.referred_count}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <p className="text-xs uppercase tracking-wide text-neutral">PathCoins earned</p>
                  <p
                    className="mt-1 text-3xl font-semibold text-primary"
                    data-testid="invite-paid-count"
                  >
                    {summary.referred_paid_count}
                  </p>
                  <p className="mt-1 text-xs text-neutral">
                    From friends who booked a paid event
                  </p>
                </CardContent>
              </Card>
            </section>
          </>
        ) : null}
      </div>
    </div>
  )
}
